import { useEffect, useState } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

type VideoState = 'paused' | 'buffering' | 'playing';

/**
 * This is a simplified set of HTML media states. See:
 * https://html.spec.whatwg.org/multipage/media.html#event-media-stalled
 */
const getVideoState = (eventType: string): VideoState => {
	if (eventType === 'playing') {
		return 'playing';
	}
	if (['waiting', 'stalled'].includes(eventType)) {
		return 'buffering';
	}
	return 'paused';
};

export const VideoStateIndicatorSunrise: React.FC<{
	videoRef: React.RefObject<HTMLVideoElement | null>;
}> = ({ videoRef }) => {
	const [videoState, setVideoState] = useState<VideoState>('paused');
	const [hasControls, setHasControls] = useState<boolean>(false);

	useEffect(() => {
		const videoEl = videoRef.current;
		if (!videoEl) {
			return () => {};
		}

		const handleEvent = (event: Event) => {
			setVideoState(getVideoState(event.type));
		};

		videoEl.addEventListener('playing', handleEvent);
		videoEl.addEventListener('pause', handleEvent);
		videoEl.addEventListener('waiting', handleEvent);
		videoEl.addEventListener('stalled', handleEvent);

		return () => {
			videoEl.removeEventListener('playing', handleEvent);
			videoEl.removeEventListener('pause', handleEvent);
			videoEl.removeEventListener('waiting', handleEvent);
			videoEl.removeEventListener('stalled', handleEvent);
		};
	}, [videoRef]);

	// Track controls value once the ref is available
	useEffect(() => {
		const videoEl = videoRef.current;
		if (!videoEl) return;

		setHasControls(!!videoEl.controls);
	}, [videoRef]);

	const isVisible = !hasControls && videoState === 'buffering';

	return (
		<div className={cn('h-[20%]', isVisible ? 'block' : 'hidden')} data-testid="video-state-indicator">
			<Loader color="typography-lightPrimary" />
		</div>
	);
};
